import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ChartPieSlice, TrendUp, TrendDown, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEUR = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} EUR`;

export const ProfitabilityWidget = ({ wrapStyle }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!projects) return null;

  // Compute profitability per project: profit = invoiced - expenses
  const rows = projects
    .filter(p => (p.total_invoiced_ht || 0) > 0 || (p.total_expenses_ht || 0) > 0)
    .map(p => {
      const invoiced = p.total_invoiced_ht || 0;
      const expenses = p.total_with_labor_ht || p.total_expenses_ht || 0;
      const profit = invoiced - expenses;
      const margin = invoiced > 0 ? (profit / invoiced) * 100 : 0;
      return { id: p.id, name: p.name, client: p.client_name, invoiced, expenses, profit, margin };
    })
    .sort((a, b) => b.profit - a.profit);

  const totalProfit = rows.reduce((s, r) => s + r.profit, 0);
  const positiveCount = rows.filter(r => r.profit > 0).length;
  const negativeCount = rows.filter(r => r.profit < 0).length;

  if (rows.length === 0) return null;

  return (
    <div style={wrapStyle}>
      <div className="card" data-testid="profitability-widget">
        <div className="card-header" style={{ backgroundColor: '#E8F5E9' }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2E7D32' }}>
            <ChartPieSlice size={20} weight="bold" /> Rentabilite par chantier
          </h3>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
            <KpiCard label="Profit cumule" value={fmtEUR(totalProfit)} color={totalProfit >= 0 ? '#2E7D32' : '#C62828'} />
            <KpiCard label="Chantiers rentables" value={positiveCount} sub={`sur ${rows.length}`} color="#2E7D32" icon={TrendUp} />
            <KpiCard label="A surveiller" value={negativeCount} sub="perte" color="#C62828" icon={TrendDown} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th>Chantier</th>
                  <th style={{ textAlign: 'right' }}>Facture</th>
                  <th style={{ textAlign: 'right' }}>Depenses</th>
                  <th style={{ textAlign: 'right' }}>Profit</th>
                  <th style={{ textAlign: 'right' }}>Marge</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 8).map(r => {
                  const profitColor = r.profit >= 0 ? '#2E7D32' : '#C62828';
                  return (
                    <tr key={r.id} data-testid={`profitability-row-${r.id}`}>
                      <td>
                        <div style={{ fontWeight: '600' }}>{r.name}</div>
                        {r.client && <div style={{ fontSize: '0.75rem', color: '#9E9E9E' }}>{r.client}</div>}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtEUR(r.invoiced)}</td>
                      <td style={{ textAlign: 'right', color: '#5E5F66' }}>{fmtEUR(r.expenses)}</td>
                      <td style={{ textAlign: 'right', fontWeight: '700', color: profitColor }}>{fmtEUR(r.profit)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: r.profit >= 0 ? '#E8F5E9' : '#FFEBEE', color: profitColor }}>
                          {r.margin.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => navigate(`/projects?tab=chantiers&openProject=${r.id}`)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5E5F66', padding: '4px' }}
                          title="Ouvrir la fiche chantier"
                        >
                          <ArrowRight size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length > 8 && (
            <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.8rem', color: '#5E5F66' }}>
              {rows.length - 8} chantier(s) supplementaire(s). Voir la section Gestion des Chantiers.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, sub, color, icon: Icon }) => (
  <div style={{ padding: '12px', backgroundColor: '#FAFAFA', border: '1px solid #E2E2E6', borderRadius: '6px' }}>
    <div style={{ fontSize: '0.75rem', color: '#5E5F66', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
      {Icon && <Icon size={12} />} {label}
    </div>
    <div style={{ fontSize: '1.25rem', fontWeight: '700', color }}>{value}</div>
    {sub && <div style={{ fontSize: '0.7rem', color: '#9E9E9E' }}>{sub}</div>}
  </div>
);
